import { useEffect, useMemo, useRef, useState } from 'react';
import { Form, message } from 'antd';
import { CircleNotchIcon, MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '@admin-panel/ui';
import { updateAttributesOfCdnImage } from '@admin-panel/utils';

import type { LookDTOType } from '../../../schemas/styling';
import type { RecommendedProductDTOType } from '../../../store/stylesSearch/schemas';
import { useGetRecommendationsMutation } from '../../../store/stylesSearch/stylesSearchApi';
import { useUpdateLookContentMutation } from '../../../store/stylist/stylistApi';
import type { VisualSearchProduct } from '../GeneratedWidgetList';
import { SectionHeading } from '../helpers';
import {
  getProductDepth,
  ProductDepthRail,
  useProductDepthByStyleNumbers,
} from '../productDepth';
import type { LookContentFormValues, SearchSeedStateItem } from '../types';

type SearchSeedsSectionProps = {
  showHeading: boolean;
  lookId?: number;
  searchSeeds: Array<SearchSeedStateItem>;
  baseProductColor?: string;
  onSearchSeedsUpdated?: (look: LookDTOType) => void;
};

const getAvailableColorFilters = (
  item: SearchSeedStateItem,
  baseProductColor?: string,
): Array<string> => {
  const seen = new Set<string>();

  return [...item.color_filters, baseProductColor || ''].filter(colorValue => {
    const normalizedValue = colorValue.trim();
    if (!normalizedValue) {
      return false;
    }

    const dedupeKey = normalizedValue.toLowerCase();
    if (seen.has(dedupeKey)) {
      return false;
    }

    seen.add(dedupeKey);
    return true;
  });
};

const normalizeDeepLink = (value: string) => {
  if (!value) {
    return '';
  }

  try {
    const parsed = new URL(value);
    return `${parsed.pathname.replace(/^\/+/, '')}${parsed.search}`;
  } catch {
    return value.replace(/^https?:\/\//, '').replace(/^\/+/, '');
  }
};

const toSearchProduct = (product: RecommendedProductDTOType): VisualSearchProduct | null => {
  const id = product.id || product.product_id || product.lk_style_number || product.title;
  const deepLink = normalizeDeepLink(product.product_link);

  if (!id || !deepLink) {
    return null;
  }

  return {
    id,
    name: product.title || product.product_type || id,
    assetUrl: product.default_image,
    deepLink,
    rank: null,
    lkStyleNumber: product.lk_style_number,
    ...getProductDepth(product),
  };
};

const buildSearchFacets = (item: SearchSeedStateItem): Record<string, Array<string>> => {
  const selectedFacets: Record<string, Array<string>> = {
    'Shopify Status': ['ACTIVE'],
  };

  if (item.product_type_filters.length > 0) {
    selectedFacets['Product Type'] = item.product_type_filters;
  }

  if (item.color_filters.length > 0) {
    selectedFacets.Color = item.color_filters;
  }

  return selectedFacets;
};

const seedItemKey = (item: SearchSeedStateItem, index: number) =>
  [item.product_type, item.search_seed, index].join('::');

const serializeSearchSeeds = (items: Array<SearchSeedStateItem>) => JSON.stringify(items);

const buildImageUrl = (value: string) =>
  value ? updateAttributesOfCdnImage(value, { width: 260, quality: 100 }) || value : '';

const productIdentifier = (product: VisualSearchProduct) =>
  product.lkStyleNumber || product.id || product.deepLink;

const mapSelectedProducts = (
  item: SearchSeedStateItem,
  depthByStyleNumber: Record<string, { inventoryDepth: number; jitDepth: number }>,
): Array<VisualSearchProduct> => {
  const mappedImages = item.mapped_product_images;
  const orderedIds = [
    ...item.mapped_lk_styles,
    ...Object.keys(mappedImages).filter(
      styleNumber => !item.mapped_lk_styles.includes(styleNumber),
    ),
  ];

  return orderedIds
    .map<VisualSearchProduct | null>(styleNumber => {
      const assetUrl = mappedImages[styleNumber] || '';
      if (!styleNumber && !assetUrl) {
        return null;
      }

      const productDepth = styleNumber ? depthByStyleNumber[styleNumber] : undefined;

      return {
        id: styleNumber || assetUrl,
        name: styleNumber || 'Mapped product',
        assetUrl,
        deepLink: '',
        rank: null,
        lkStyleNumber: styleNumber,
        inventoryDepth: productDepth?.inventoryDepth,
        jitDepth: productDepth?.jitDepth,
      };
    })
    .filter((product): product is VisualSearchProduct => product !== null);
};

const ProductTile = ({
  product,
  removable = false,
  onClick,
  disabled = false,
}: {
  product: VisualSearchProduct;
  removable?: boolean;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <div className="group relative w-fit">
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        'relative block aspect-2/3 h-36 overflow-hidden rounded-sm bg-muted/20 transition',
        disabled ? 'cursor-progress opacity-60' : 'hover:cursor-pointer hover:shadow-md',
      ].join(' ')}
      title={product.name || product.id}
      aria-label={removable ? `Remove ${product.name}` : `Select ${product.name}`}
    >
      {product.assetUrl ? (
        <img
          src={buildImageUrl(product.assetUrl)}
          alt={product.name || product.id}
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center px-2 text-xs text-muted-foreground">
          {product.lkStyleNumber || product.id || 'No image'}
        </div>
      )}

      <ProductDepthRail inventoryDepth={product.inventoryDepth} jitDepth={product.jitDepth} />
    </button>

    {removable ? (
      <Button
        variant="outline"
        color="neutral"
        size="small"
        className="absolute -right-1 -top-1 z-10 h-6 min-h-0 w-6 min-w-0 rounded-full p-0 opacity-0 shadow-sm transition-opacity group-hover:opacity-100"
        onClick={event => {
          event.stopPropagation();
          onClick();
        }}
        isDisabled={disabled}
        aria-label={`Remove ${product.name}`}
      >
        <XIcon size={12} />
      </Button>
    ) : null}
  </div>
);

const SearchTile = ({ isLoading, onClick }: { isLoading: boolean; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={isLoading}
    className={[
      'flex aspect-2/3 h-36 flex-col items-center justify-center rounded-sm border border-dashed border-border/70 bg-muted/20 px-3 text-center transition',
      isLoading
        ? 'hover:cursor-progress'
        : 'hover:cursor-pointer hover:border-foreground/40 hover:bg-muted/30 hover:shadow-sm',
    ].join(' ')}
  >
    <MagnifyingGlassIcon size={22} />
    <span className="mt-2 text-xs font-medium text-foreground">
      {isLoading ? 'Searching...' : 'Search Products'}
    </span>
  </button>
);

const SelectablePill = ({
  label,
  selected,
  onClick,
  disabled = false,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={[
      'rounded-full border px-3 py-1.5 text-sm transition',
      disabled ? 'cursor-progress opacity-60' : '',
      selected
        ? 'border-foreground/70 bg-foreground text-background hover:border-foreground hover:bg-foreground/90'
        : 'border-border/70 text-foreground hover:cursor-pointer hover:border-foreground/40 hover:bg-muted/30',
    ].join(' ')}
  >
    {label}
  </button>
);

const SearchSeedsSection = ({
  showHeading,
  lookId,
  searchSeeds,
  baseProductColor,
  onSearchSeedsUpdated,
}: SearchSeedsSectionProps) => {
  const form = Form.useFormInstance<LookContentFormValues>();
  const [triggerRecommendations] = useGetRecommendationsMutation();
  const [updateLookContent] = useUpdateLookContentMutation();
  const [removedSeedKeys, setRemovedSeedKeys] = useState<Array<string>>([]);
  const [searchResultsBySeed, setSearchResultsBySeed] = useState<
    Record<string, Array<VisualSearchProduct>>
  >({});
  const [searchingSeedKeys, setSearchingSeedKeys] = useState<Record<string, boolean>>({});
  const [searchErrors, setSearchErrors] = useState<Record<string, string>>({});
  const [selectedQueryBySeed, setSelectedQueryBySeed] = useState<Record<string, string>>({});
  const [useProductTypeBySeed, setUseProductTypeBySeed] = useState<Record<string, boolean>>({});
  const [selectedProductTypeFiltersBySeed, setSelectedProductTypeFiltersBySeed] = useState<
    Record<string, Array<string>>
  >({});
  const [selectedColorFiltersBySeed, setSelectedColorFiltersBySeed] = useState<
    Record<string, Array<string>>
  >({});
  const [autosaveState, setAutosaveState] = useState<
    'idle' | 'pending' | 'saving' | 'saved' | 'error'
  >('idle');
  const hasInitializedSaveRef = useRef(false);
  const [draftSearchSeeds, setDraftSearchSeeds] = useState(searchSeeds);
  const lastSavedRef = useRef(serializeSearchSeeds(searchSeeds));
  const autosaveTimerRef = useRef<number | null>(null);
  const searchTimerRefs = useRef<Record<string, number>>({});

  const seedItems = useMemo(
    () =>
      draftSearchSeeds.map((item, index) => ({
        item,
        key: seedItemKey(item, index),
      })),
    [draftSearchSeeds],
  );
  const selectedStyleNumbers = useMemo(
    () =>
      draftSearchSeeds
        .flatMap(item => [
          ...(item.mapped_lk_styles || []),
          ...Object.keys(item.mapped_product_images || {}),
        ])
        .map(String)
        .filter(Boolean),
    [draftSearchSeeds],
  );
  const depthByStyleNumber = useProductDepthByStyleNumbers(selectedStyleNumbers);

  const seedItemMap = useMemo(
    () => new Map(seedItems.map(entry => [entry.key, entry.item])),
    [seedItems],
  );

  const serializedSearchSeeds = useMemo(
    () => serializeSearchSeeds(draftSearchSeeds),
    [draftSearchSeeds],
  );
  const serializedIncomingSearchSeeds = useMemo(
    () => serializeSearchSeeds(searchSeeds),
    [searchSeeds],
  );

  useEffect(() => {
    setRemovedSeedKeys([]);
  }, [lookId]);

  useEffect(() => {
    setDraftSearchSeeds(searchSeeds);
    lastSavedRef.current = serializedIncomingSearchSeeds;
  }, [lookId, serializedIncomingSearchSeeds]);

  useEffect(
    () => () => {
      Object.values(searchTimerRefs.current).forEach(timerId => window.clearTimeout(timerId));
    },
    [],
  );

  useEffect(() => {
    setRemovedSeedKeys(previous => previous.filter(key => seedItemMap.has(key)));

    setSearchResultsBySeed(previous =>
      Object.fromEntries(Object.entries(previous).filter(([key]) => seedItemMap.has(key))),
    );
    setSearchErrors(previous =>
      Object.fromEntries(Object.entries(previous).filter(([key]) => seedItemMap.has(key))),
    );
    setSelectedQueryBySeed(previous =>
      seedItems.reduce<Record<string, string>>((nextState, { item, key }) => {
        nextState[key] = previous[key] && item.queries.includes(previous[key]) ? previous[key] : '';
        return nextState;
      }, {}),
    );
    setUseProductTypeBySeed(previous =>
      seedItems.reduce<Record<string, boolean>>((nextState, { key }) => {
        nextState[key] = previous[key] ?? false;
        return nextState;
      }, {}),
    );
    setSelectedProductTypeFiltersBySeed(previous =>
      seedItems.reduce<Record<string, Array<string>>>((nextState, { item, key }) => {
        const previousValues = previous[key];
        nextState[key] =
          previousValues?.filter(value => item.product_type_filters.includes(value)) ?? [];
        return nextState;
      }, {}),
    );
    setSelectedColorFiltersBySeed(previous =>
      seedItems.reduce<Record<string, Array<string>>>((nextState, { item, key }) => {
        const previousValues = previous[key];
        const availableColorFilters = getAvailableColorFilters(item, baseProductColor);
        nextState[key] =
          previousValues?.filter(value => availableColorFilters.includes(value)) ?? [];
        return nextState;
      }, {}),
    );
  }, [baseProductColor, seedItemMap, seedItems]);

  useEffect(() => {
    if (!lookId) {
      return;
    }

    if (!hasInitializedSaveRef.current) {
      hasInitializedSaveRef.current = true;
      lastSavedRef.current = serializedSearchSeeds;
      return;
    }

    if (serializedSearchSeeds === lastSavedRef.current) {
      return;
    }

    setAutosaveState('pending');

    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
    }

    autosaveTimerRef.current = window.setTimeout(async () => {
      try {
        setAutosaveState('saving');
        const updatedLook = await updateLookContent({
          id: lookId,
          data: {
            search_seeds: draftSearchSeeds,
          },
        }).unwrap();

        const normalizedSearchSeeds = Array.isArray(updatedLook.search_seeds)
          ? (updatedLook.search_seeds as Array<SearchSeedStateItem>)
          : [];

        setDraftSearchSeeds(normalizedSearchSeeds);
        form.setFieldsValue({ search_seeds: normalizedSearchSeeds });
        lastSavedRef.current = serializeSearchSeeds(normalizedSearchSeeds);
        onSearchSeedsUpdated?.(updatedLook);
        setAutosaveState('saved');
      } catch (error) {
        console.error('Error updating search seeds:', error);
        setAutosaveState('error');
        message.error('Failed to update search seeds');
      }
    }, 500);

    return () => {
      if (autosaveTimerRef.current) {
        window.clearTimeout(autosaveTimerRef.current);
      }
    };
  }, [
    draftSearchSeeds,
    form,
    lookId,
    onSearchSeedsUpdated,
    serializedSearchSeeds,
    updateLookContent,
  ]);

  const updateSearchSeeds = (
    updater: (current: Array<SearchSeedStateItem>) => Array<SearchSeedStateItem>,
  ) => {
    const nextSearchSeeds = updater(draftSearchSeeds);
    setDraftSearchSeeds(nextSearchSeeds);
    form.setFieldsValue({ search_seeds: nextSearchSeeds });
  };

  const applySeed = (seedKey: string) => {
    setRemovedSeedKeys(previous => previous.filter(key => key !== seedKey));
  };

  const selectQuery = (seedKey: string, query: string) => {
    setSelectedQueryBySeed(previous => ({
      ...previous,
      [seedKey]: previous[seedKey] === query ? '' : query,
    }));
  };

  const toggleProductTypeFilter = (seedKey: string, filterValue: string) => {
    setSelectedProductTypeFiltersBySeed(previous => {
      const currentValues = previous[seedKey] || [];
      const nextValues = currentValues.includes(filterValue)
        ? currentValues.filter(value => value !== filterValue)
        : [...currentValues, filterValue];

      return {
        ...previous,
        [seedKey]: nextValues,
      };
    });
  };

  const toggleColorFilter = (seedKey: string, colorValue: string) => {
    setSelectedColorFiltersBySeed(previous => {
      const currentValues = previous[seedKey] || [];
      const nextValues = currentValues.includes(colorValue)
        ? currentValues.filter(value => value !== colorValue)
        : [...currentValues, colorValue];

      return {
        ...previous,
        [seedKey]: nextValues,
      };
    });
  };

  const toggleProduct = (seedKey: string, product: VisualSearchProduct) => {
    const nextIdentifier = productIdentifier(product);
    if (!nextIdentifier) {
      return;
    }

    updateSearchSeeds(current =>
      current.map((item, index) => {
        if (seedItemKey(item, index) !== seedKey) {
          return item;
        }

        const currentMappedIds = (item.mapped_lk_styles || []).map(String);
        const isSelected = currentMappedIds.includes(nextIdentifier);
        const nextMappedIds = isSelected
          ? currentMappedIds.filter(value => value !== nextIdentifier)
          : [...currentMappedIds, nextIdentifier];
        const nextMappedImages = {
          ...(item.mapped_product_images || {}),
        };

        if (isSelected) {
          delete nextMappedImages[nextIdentifier];
        } else if (product.assetUrl) {
          nextMappedImages[nextIdentifier] = product.assetUrl;
        }

        return {
          ...item,
          mapped_lk_styles: nextMappedIds,
          mapped_product_images: nextMappedImages,
        };
      }),
    );
  };

  const handleSearch = async (seedKey: string) => {
    const item = seedItemMap.get(seedKey);
    if (!item || searchingSeedKeys[seedKey]) {
      return;
    }

    const selectedQuery = selectedQueryBySeed[seedKey] || '';
    const selectedProductTypeFilters = selectedProductTypeFiltersBySeed[seedKey] || [];
    const selectedColorFilters = selectedColorFiltersBySeed[seedKey] || [];
    const shouldUseProductType = Boolean(useProductTypeBySeed[seedKey]);
    const hasSelectedInput =
      shouldUseProductType ||
      Boolean(selectedQuery) ||
      selectedProductTypeFilters.length > 0 ||
      selectedColorFilters.length > 0;
    const text = [shouldUseProductType ? item.product_type.replace(/_/g, ' ') : '', selectedQuery]
      .filter(Boolean)
      .join(', ');

    if (!hasSelectedInput) {
      message.warning('Select a query, product type, product filter, or color to search.');
      return;
    }

    if (searchTimerRefs.current[seedKey]) {
      window.clearTimeout(searchTimerRefs.current[seedKey]);
    }

    setSearchingSeedKeys(previous => ({ ...previous, [seedKey]: true }));
    setSearchErrors(previous => ({ ...previous, [seedKey]: '' }));

    searchTimerRefs.current[seedKey] = window.setTimeout(async () => {
      try {
        const response = await triggerRecommendations({
          searchType: 'FREETEXT',
          text,
          selectedFacets: buildSearchFacets({
            ...item,
            product_type_filters: selectedProductTypeFilters,
            color_filters: selectedColorFilters,
          }),
        }).unwrap();

        const nextProducts = (response.products || [])
          .map(toSearchProduct)
          .filter((product): product is VisualSearchProduct => product !== null);

        setSearchResultsBySeed(previous => ({
          ...previous,
          [seedKey]: nextProducts,
        }));
      } catch (error) {
        console.error('Error searching products for search seed:', error);
        setSearchErrors(previous => ({
          ...previous,
          [seedKey]: 'Could not fetch products for this seed.',
        }));
        message.error('Failed to search products');
      } finally {
        delete searchTimerRefs.current[seedKey];
        setSearchingSeedKeys(previous => ({ ...previous, [seedKey]: false }));
      }
    }, 350);
  };

  const removedSeedKeySet = new Set(removedSeedKeys);
  const appliedSeeds = seedItems.filter(({ key }) => !removedSeedKeySet.has(key));
  const availableSeeds = seedItems.filter(({ key }) => removedSeedKeySet.has(key));
  const isPersistingSelection = autosaveState === 'pending' || autosaveState === 'saving';

  return (
    <section className="space-y-4">
      {showHeading ? <SectionHeading title="Search Seeds" /> : null}

      {autosaveState === 'error' ? (
        <p className="m-0 text-xs text-rose-700">Failed to save search-seed selections.</p>
      ) : null}

      {availableSeeds.length > 0 ? (
        <section className="space-y-2">
          <p className="m-0 text-sm font-medium text-foreground">Available Search Seeds</p>
          <div className="flex flex-wrap gap-2">
            {availableSeeds.map(({ item, key }) => (
              <button
                key={key}
                type="button"
                disabled={isPersistingSelection}
                onClick={() => applySeed(key)}
                className={[
                  'rounded-full border border-border/70 px-3 py-1.5 text-sm text-foreground transition',
                  isPersistingSelection
                    ? 'cursor-progress opacity-60'
                    : 'hover:cursor-pointer hover:border-foreground/40 hover:bg-muted/30',
                ].join(' ')}
              >
                {item.product_type.replace(/_/g, ' ') || 'Search seed'}
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {appliedSeeds.length > 0 ? (
        <section className="space-y-4">
          {appliedSeeds.map(({ item, key }) => {
            const isSearching = Boolean(searchingSeedKeys[key]);
            const selectedProducts = mapSelectedProducts(item, depthByStyleNumber);
            const searchResults = searchResultsBySeed[key] || [];
            const isBusy = Boolean(searchingSeedKeys[key]) || isPersistingSelection;
            const selectedIdentifiers = new Set(
              selectedProducts.map(productIdentifier).filter(Boolean),
            );
            const visibleSearchResults = searchResults.filter(
              product => !selectedIdentifiers.has(productIdentifier(product)),
            );
            const selectedQuery = selectedQueryBySeed[key] || '';
            const selectedColorFilters = selectedColorFiltersBySeed[key] || [];
            const availableColorFilters = getAvailableColorFilters(item, baseProductColor);

            return (
              <article
                key={key}
                className="space-y-3 rounded-sm border border-border/50 bg-background p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <p className="m-0 text-sm font-medium capitalize text-foreground">
                      {(item.product_type || 'Search seed').replace(/_/g, ' ')}
                    </p>
                    {item.description ? (
                      <p className="m-0 text-sm leading-6 text-muted-foreground">
                        {item.description}
                      </p>
                    ) : null}
                  </div>
                </div>

                {item.queries.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Query</p>
                    <div className="flex flex-wrap gap-2">
                      {item.queries.map(query => (
                        <SelectablePill
                          key={`${key}-query-${query}`}
                          label={query}
                          selected={selectedQuery === query}
                          disabled={isBusy}
                          onClick={() => selectQuery(key, query)}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}

                {item.product_type_filters.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Product Filters</p>
                    <div className="flex flex-wrap gap-2">
                      {item.product_type_filters.map(filterValue => (
                        <SelectablePill
                          key={`${key}-filter-${filterValue}`}
                          label={filterValue}
                          selected={(selectedProductTypeFiltersBySeed[key] || []).includes(
                            filterValue,
                          )}
                          disabled={isBusy}
                          onClick={() => toggleProductTypeFilter(key, filterValue)}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}

                {availableColorFilters.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Color</p>
                    <div className="flex flex-wrap gap-2">
                      {availableColorFilters.map(colorValue => (
                        <SelectablePill
                          key={`${key}-color-${colorValue}`}
                          label={colorValue}
                          selected={selectedColorFilters.includes(colorValue)}
                          disabled={isBusy}
                          onClick={() => toggleColorFilter(key, colorValue)}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}

                <section className="space-y-2 rounded-sm bg-muted/10 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="m-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Selected Products
                    </p>
                    {isSearching ? (
                      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                        <CircleNotchIcon size={14} className="animate-spin" />
                        Searching products
                      </span>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-start gap-2">
                    {selectedProducts.map(product => (
                      <ProductTile
                        key={`${key}-${productIdentifier(product)}`}
                        product={product}
                        removable={true}
                        disabled={isBusy}
                        onClick={() => toggleProduct(key, product)}
                      />
                    ))}

                    <SearchTile isLoading={isBusy} onClick={() => void handleSearch(key)} />
                  </div>
                </section>

                {searchErrors[key] ? (
                  <p className="m-0 text-xs text-rose-700">{searchErrors[key]}</p>
                ) : null}

                {isSearching || visibleSearchResults.length > 0 ? (
                  <section className="space-y-2 border-t border-border/50 pt-3">
                    <p className="m-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Search Results
                    </p>
                    {isSearching ? (
                      <div className="flex min-h-36 items-center gap-2 text-sm text-muted-foreground">
                        <CircleNotchIcon size={16} className="animate-spin" />
                        Fetching products...
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-start gap-2">
                        {visibleSearchResults.map(product => (
                          <ProductTile
                            key={`${key}-result-${productIdentifier(product)}`}
                            product={product}
                            disabled={isBusy}
                            onClick={() => toggleProduct(key, product)}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                ) : null}
              </article>
            );
          })}
        </section>
      ) : (
        <div className="rounded-sm border border-dashed border-border/70 px-4 py-6 text-sm text-muted-foreground">
          Apply a search-seed preset to start finding products.
        </div>
      )}
    </section>
  );
};

export default SearchSeedsSection;
